<script setup lang="ts">
// フィルターダイアログ: スキーマからコントロールを生成し、ライブプレビューする。
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { useModal } from '@/composables/useModalStack'
import { useI18n } from '@/i18n'
import { getFilter, defaultParams, type FilterParams } from '@/filters/registry'
import { previewFilter, clearFilterPreview } from '@/app/engine'
import { runCommand } from '@/commands/registry'
import SliderField from '@/components/common/SliderField.vue'

const props = defineProps<{ filterId: string }>()
const emit = defineEmits<{ close: [] }>()
useModal('filter')
const { t } = useI18n()
const docStore = useDocumentStore()
const def = getFilter(props.filterId)!
const layerId = docStore.activeLayer?.id ?? ''
const params = ref<FilterParams>(defaultParams(def))
const preview = ref(true)
const busy = ref(false)

const working = ref(false)
let timer: number | null = null
function schedulePreview() {
  if (timer !== null) clearTimeout(timer)
  // CPU (Worker) フィルターは重いので長めに待つ
  timer = window.setTimeout(async () => {
    timer = null
    if (preview.value) {
      working.value = true
      try {
        await previewFilter(layerId, def.id, params.value)
      } finally {
        working.value = false
      }
    } else clearFilterPreview()
  }, def.cpu ? 150 : 30)
}
watch(params, schedulePreview, { deep: true, immediate: true })
watch(preview, schedulePreview)
onBeforeUnmount(() => {
  if (timer !== null) clearTimeout(timer)
  clearFilterPreview()
})
async function ok() {
  if (busy.value) return
  busy.value = true
  if (timer !== null) {
    clearTimeout(timer)
    timer = null
  }
  try {
    clearFilterPreview()
    await runCommand('filter.apply', { id: layerId, filter: def.id, params: { ...params.value } })
  } finally {
    busy.value = false
  }
  emit('close')
}
function cancel() {
  clearFilterPreview()
  emit('close')
}
const numberParams = computed(() => def.params)
</script>

<template>
  <div class="modal-backdrop" @pointerdown.self="cancel">
    <div class="modal filter" @keydown.esc="cancel" @keydown.enter="ok">
      <h2>{{ t(def.label).replace(/\.\.\.$/, '') }}</h2>
      <div class="params">
        <template v-for="p in numberParams" :key="p.key">
          <SliderField v-if="p.type === 'number'" :label="t(p.label)" :value="Number(params[p.key])" :min="p.min" :max="p.max" :step="p.step" :unit="p.unit ?? ''" @input="v => (params[p.key] = v)" @change="v => (params[p.key] = v)" />
          <label v-else-if="p.type === 'boolean'" class="row"><input type="checkbox" :checked="!!params[p.key]" @change="params[p.key] = ($event.target as HTMLInputElement).checked" /><span>{{ t(p.label) }}</span></label>
          <label v-else class="row"><span class="muted">{{ t(p.label) }}</span>
            <select :value="String(params[p.key])" @change="params[p.key] = ($event.target as HTMLSelectElement).value">
              <option v-for="o in p.options" :key="o.value" :value="o.value">{{ t(o.label) }}</option>
            </select>
          </label>
        </template>
        <p v-if="!def.params.length" class="muted">—</p>
      </div>
      <label class="row"><input v-model="preview" type="checkbox" /><span>{{ t('filter.preview') }}</span><span v-if="working || busy" class="muted working">{{ t('filter.working') }}</span></label>
      <div class="actions">
        <button @click="cancel">{{ t('dialog.cancel') }}</button>
        <button class="primary" :disabled="busy" @click="ok">{{ t('dialog.ok') }}</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.filter {
  min-width: 380px;
}
.working {
  margin-left: auto;
  font-size: 11px;
}
.params {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
}
</style>
